import { __awaiter, __generator } from "tslib";
import { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { publishComment } from '../redux/actionCreators';
import { useVerification } from '../../VerificationPopup/withVerification';
import { CommentEditorContext, EditorContext } from '../context';
import { useEditorDraftActions } from './useEditorDraftActions';
import { CommentEditorSource } from '../redux/types';
/**
 * Provides a function that will post a comment!
 *
 * Relies on all the contexts provided by `<CommentEditorProviders />`,
 * so be sure you have it set up first!
 */
export function useCommentPostAction(_a) {
    var _this = this;
    var _b = _a === void 0 ? {} : _a, messageId = _b.messageId, folderId = _b.folderId, shouldDeleteMessage = _b.shouldDeleteMessage, onPost = _b.onPost;
    var _c = useContext(CommentEditorContext), isBusy = _c.isBusy, editorId = _c.editorId, commentableType = _c.commentableType, commentableItemId = _c.commentableItemId, editorSource = _c.editorSource, relevantComment = _c.relevantComment;
    var _d = useContext(EditorContext), editorApi = _d.api, editorState = _d.state;
    var dispatch = useDispatch();
    var _e = useVerification(), pushVerificationPopup = _e.pushVerificationPopup, isVerificationNeeded = _e.isVerificationNeeded;
    var removeEditorDraft = useEditorDraftActions(editorId).removeEditorDraft;
    var logBiEvent = useBiLogger();
    var postComment = useCallback(function () { return __awaiter(_this, void 0, void 0, function () {
        var state;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (isBusy) {
                        return [2 /*return*/];
                    }
                    if (isVerificationNeeded) {
                        pushVerificationPopup('comment');
                        return [2 /*return*/];
                    }
                    // Clean up draft state in local storage
                    removeEditorDraft();
                    if (editorSource !== CommentEditorSource.Edit) {
                        logBiEvent({
                            evid: BiEventType.COMMENT_BUTTON_CLICK,
                            itemid: typeof commentableItemId === 'string'
                                ? parseInt(commentableItemId)
                                : commentableItemId,
                            typeid: commentableType,
                            is_reply: editorSource === CommentEditorSource.Reply ? 1 : 0,
                        });
                    }
                    return [4 /*yield*/, (editorApi === null || editorApi === void 0 ? void 0 : editorApi.resolveEditorStateForAndroid())];
                case 1:
                    state = _a.sent();
                    if (state === null || state === void 0 ? void 0 : state.rawContentState) {
                        dispatch(publishComment({
                            editorId: editorId,
                            typeId: commentableType,
                            itemId: commentableItemId,
                            rawContentState: state.rawContentState,
                            editorContent: state.editorContent,
                            featuresData: state.features,
                            editComment: editorSource === CommentEditorSource.Edit
                                ? relevantComment
                                : undefined,
                            replyComment: editorSource === CommentEditorSource.Reply
                                ? relevantComment
                                : undefined,
                            messageId: messageId,
                            folderId: folderId,
                            shouldDeleteMessage: shouldDeleteMessage,
                        }));
                    }
                    else {
                        // Non Android codepath
                        dispatch(publishComment({
                            editorId: editorId,
                            typeId: commentableType,
                            itemId: commentableItemId,
                            rawContentState: editorState.rawContentState,
                            editorContent: editorState.editorContent,
                            featuresData: editorState.features,
                            editComment: editorSource === CommentEditorSource.Edit
                                ? relevantComment
                                : undefined,
                            replyComment: editorSource === CommentEditorSource.Reply
                                ? relevantComment
                                : undefined,
                            messageId: messageId,
                            folderId: folderId,
                            shouldDeleteMessage: shouldDeleteMessage,
                        }));
                    }
                    onPost && onPost();
                    return [2 /*return*/];
            }
        });
    }); }, [
        dispatch,
        isBusy,
        editorId,
        editorSource,
        relevantComment,
        commentableType,
        commentableItemId,
        pushVerificationPopup,
        isVerificationNeeded,
        editorApi,
        editorState,
        removeEditorDraft,
        logBiEvent,
        messageId,
        shouldDeleteMessage,
        folderId,
        onPost,
    ]);
    return postComment;
}
//# sourceMappingURL=useCommentPostAction.js.map